/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid2, Typography, Checkbox,   IconButton, Divider, Paper, Collapse, List, ListItem, ListItemText, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ReactComponent as PersonOutlineIcon } from '../../../../assets/SVG/mage_user.svg';
import CheckIcon from '@mui/icons-material/Check';
import { getUsersByRole, getModules, postUserPermissions, postRolePermissions, getPermissionsByRole, getPermissionsByUser, RoleWithUsers, ModulesResponse, GrantedUserPermission, GrantedRolePermission, Permission, GrantedPermisssionsObject } from './userRight.helper';
import UserRightsTable from './UserRightsTable';
import { useTranslation } from 'react-i18next';
import ViewMode from './ViewMode';
import { ConfirmationModal } from '../../../../components/confirmationModal';
import { useAlert } from '../../../../components/alertProvider';
import { getButtonStates } from '../../../../utils/permissionService';
import { Button } from '@mui/material';
import LoaderComponent from '../../../../components/loaderProvider/loaderComponet';
import { useLoader } from '../../../../components/loaderProvider/loader';

// Reusable Save/Next button group component
type SaveNextButtonGroupProps = {
  disabled: boolean;
  onSave: () => void;
  onNext?: () => void;
  showNext?: boolean;
  saveLabel?: string;
  nextLabel?: string;
  selectAllChecked?: boolean;
  selectAllIndeterminate?: boolean;
  onSelectAllChange?: (checked: boolean) => void;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  deselectAllLabel?: string;
};


const SaveNextButtonGroup = ({
  disabled,
  onSave,
  onNext,
  showNext = true,
  saveLabel = 'Save',
  nextLabel = 'Next',
  selectAllChecked = false,
  selectAllIndeterminate = false,
  onSelectAllChange,
  showSelectAll = false,
  selectAllLabel = 'Select All',
  deselectAllLabel = 'Deselect All',
}: SaveNextButtonGroupProps) => (
  <Box sx={{ gridColumn: '1 / -1' }}>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', width: '100%', mt: { xs: 2, md: 0 } }}>
      {showSelectAll && onSelectAllChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto' }}>
          <Checkbox
            checked={selectAllChecked}
            indeterminate={selectAllIndeterminate}
            onChange={(e) => onSelectAllChange(e.target.checked)}
            icon={<Box sx={{ border: '2px solid #E0E0E0', width: 24, height: 24, bgcolor: '#fff' }} />}
            checkedIcon={<Box sx={{ border: '2px solid #B6E2C2', width: 24, height: 24, bgcolor: '#E6F7EA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
            </Box>}
            indeterminateIcon={<Box sx={{ border: '2px solid #B6E2C2', width: 24, height: 24, bgcolor: '#E6F7EA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: 12, height: 3, bgcolor: '#4CAF50' }} />
            </Box>}
            sx={{ p: 0, mr: 1 }}
          />
          <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 500, color: '#222', fontSize: { xs: 12, md: 14 } }}>
            {selectAllChecked ? deselectAllLabel : selectAllLabel}
          </Typography>
        </Box>
      )}
      <Button
        variant="outlined"
        disabled={disabled}
        onClick={onSave}
        sx={{
          border: '1.5px solid #222',
          borderRadius: 2,
          px: 4,
          py: 1.5,
          fontFamily: 'Montserrat',
          fontWeight: 700,
          color: '#555',
          fontSize: { xs: 16, md: 18 },
          boxShadow: 'none',
        }}
      >
        {saveLabel}
      </Button>
      {showNext && (
        <Button
          variant="outlined"
          disabled={disabled || !onNext}
          onClick={onNext}
          sx={{
            border: '1.5px solid #222',
            borderRadius: 2,
            px: 4,
            py: 1.5,
            fontFamily: 'Montserrat',
            fontWeight: 700,
            color: '#555',
            fontSize: { xs: 16, md: 18 },
            boxShadow: 'none',
          }}
        >
          {nextLabel}
        </Button>
      )}
    </Box>
  </Box>
);




const UserRights: React.FC = () => {
  const [rolesData, setRolesData] = useState<RoleWithUsers[]>([]);
  const [modulesData, setModulesData] = useState<ModulesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedRoles, setExpandedRoles] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [selectedRoleName, setSelectedRoleName] = useState<{name: string; id: number} | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedModules, setSelectedModules] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState< { id: number; name: string; moduleId: number; moduleName: string }[] >([]);
  const [selectedTypes, setSelectedTypes] = useState< { id: number; name: string; categoryId: number; categoryName: string; moduleId: number; moduleName: string, name_label: string }[] >([]);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [showViewMode, setShowViewMode] = useState(false);
  const [rolePermissions, setRolePermissions] = useState<any>(null);
  const {t} = useTranslation('user_config');
  const {t:tGeneral} = useTranslation('general');
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [grantedPermissions, setGrantedPermissions] = useState<GrantedPermisssionsObject | null>(null);
  const { canEdit, canAdd } = getButtonStates('Settings', 'Configuration', 'User');
  const { showLoader, hideLoader } = useLoader();
  
  const { addAlert } = useAlert();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rolesResponse, modulesResponse] = await Promise.all([
        getUsersByRole(),
        getModules()
      ]);
      setRolesData(rolesResponse);
      setModulesData(modulesResponse);
    } catch (error) {
    // eslint-disable-next-line no-console
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, tGeneral]);

  const stepperSteps = [
    { label:t('UserRights.Module')},
    { label: t('UserRights.Category') },
    { label: t('UserRights.SubCategory') },
  ];

  // Memoized calculations for Categories (Step 1) select all state
  const availableCategories = useMemo(() => {
    if (!modulesData) return [];
    return modulesData.objects
      .filter((mod) => selectedModules.includes(mod.id))
      .flatMap((mod) => (mod.categories || []).map((cat) => ({ id: cat.id, moduleId: mod.id })));
  }, [modulesData, selectedModules]);

  const categoriesSelectAllChecked = useMemo(() => {
    return availableCategories.length > 0 && 
      availableCategories.every((cat) => 
        selectedCategories.some((c) => c.id === cat.id && c.moduleId === cat.moduleId)
      );
  }, [availableCategories, selectedCategories]);

  const categoriesSelectAllIndeterminate = useMemo(() => {
    const selectedCount = availableCategories.filter((cat) => 
      selectedCategories.some((c) => c.id === cat.id && c.moduleId === cat.moduleId)
    ).length;
    return selectedCount > 0 && selectedCount < availableCategories.length;
  }, [availableCategories, selectedCategories]);

  // Memoized calculations for Types (Step 2) select all state
  const availableTypes = useMemo(() => {
    if (!modulesData) return [];
    return modulesData.objects
      .filter((mod) => selectedModules.includes(mod.id))
      .flatMap((mod) => (mod.categories || [])
        .filter((cat) => selectedCategories.some((c) => c.id === cat.id && c.moduleId === mod.id))
        .flatMap((cat) => (cat.types || []).map((type) => ({
          id: type.id,
          categoryId: cat.id,
          moduleId: mod.id
        })))
      );
  }, [modulesData, selectedModules, selectedCategories]);

  const typesSelectAllChecked = useMemo(() => {
    return availableTypes.length > 0 && 
      availableTypes.every((type) => 
        selectedTypes.some((t) => t.id === type.id && t.categoryId === type.categoryId && t.moduleId === type.moduleId)
      );
  }, [availableTypes, selectedTypes]);

  const typesSelectAllIndeterminate = useMemo(() => {
    const selectedCount = availableTypes.filter((type) => 
      selectedTypes.some((t) => t.id === type.id && t.categoryId === type.categoryId && t.moduleId === type.moduleId)
    ).length;
    return selectedCount > 0 && selectedCount < availableTypes.length;
  }, [availableTypes, selectedTypes]);
  
  const getCategoriesForModule = (modIdx: number) => {
    if (!modulesData) return [];
    const mod = modulesData.objects[modIdx];
    return (mod.categories || []).map((cat) => ({
      id: cat.id,
      name: cat.name,
      moduleId: mod.id,
      moduleName: mod.name
    }));
  };
  const getTypesForModule = (modIdx: number) => {
    if (!modulesData) return [];
    const mod = modulesData.objects[modIdx];
    return (mod.categories || []).flatMap((cat) =>
      (cat.types || []).map((type) => ({
        id: type.id,
        name: type.name,
        name_label: type.name_label,
        categoryId: cat.id,
        categoryName: cat.name,
        moduleId: mod.id,
        moduleName: mod.name
      }))
    );
  };
  const getTypesForCategory = (modIdx: number, catId: number) => {
    if (!modulesData) return [];
    const mod = modulesData.objects[modIdx];
    const cat = (mod.categories || []).find((c) => c.id === catId);
    if (!cat) return [];
    return (cat.types || []).map((type) => ({
      id: type.id,
      name: type.name,
      name_label: type.name_label,
      categoryId: cat.id,
      categoryName: cat.name,
      moduleId: mod.id,
      moduleName: mod.name
    }));
  };

  const handleExpand = (roleId: number) => {
    setExpandedRoles(prev => (prev.includes(roleId) ? [] : [roleId]));
  };

  // Helper function to normalize actions
  const normalizeActions = (actions: string[] | string): string[] => {
    const arr = Array.isArray(actions) ? actions : [actions];
    return arr.map(a => a.toLowerCase()).sort();
  };

  // Helper function to create group key for permission comparison
  const createGroupKey = (perm: any): string => {
    let actions: string[] = [];
    if (perm && perm.action) {
      actions = normalizeActions(perm.action);
    }
    return JSON.stringify({ actions, male: perm.male, female: perm.female });
  };

  // Reusable function to process permissions response
  const processPermissionsResponse = (permissionsResponse: any) => {
    if (!modulesData || !permissionsResponse || !Array.isArray(permissionsResponse.permissions)) {
      return null;
    }

    let remainingPermissions: any[] = [...permissionsResponse.permissions];
    const moduleLevelPermissions: Array<{ Name: string; name_label: string; actions: string[]; male: any; female: any; id: number }> = [];
    const categoryLevelPermissions: Array<{ Name: string; name_label: string; module: string; actions: string[]; male: any; female: any; id: number }> = [];

    // 1. Try to aggregate at module level
    modulesData.objects.forEach((mod) => {
      const allTypeIds = (mod.categories || []).flatMap(cat => (cat.types || []).map(type => type.id));
      const permsForTypes = allTypeIds.map(typeId =>
        remainingPermissions.find((perm: any) => (perm.type || perm.type_id) === typeId)
      ).filter(Boolean);
      
      if (permsForTypes.length === allTypeIds.length && permsForTypes.length > 0) {
        const firstKey = createGroupKey(permsForTypes[0]);
        const allSame = permsForTypes.every(perm => createGroupKey(perm) === firstKey);
        if (allSame) {
          const { male, female } = permsForTypes[0];
          const actions = permsForTypes[0]?.action ? normalizeActions(permsForTypes[0].action) : [];
          remainingPermissions = remainingPermissions.filter((perm: any) => !allTypeIds.includes(perm.type || perm.type_id));
          moduleLevelPermissions.push({ Name: mod.name, name_label: mod.name_label, actions, male, female, id: mod.id });
        }
      }
    });

    // 2. Try to aggregate at category level
    modulesData.objects.forEach((mod) => {
      if (moduleLevelPermissions.find(m => m.Name === mod.name)) return;
      (mod.categories || []).forEach((cat) => {
        const allTypeIds = (cat.types || []).map(type => type.id);
        const permsForTypes = allTypeIds.map(typeId =>
          remainingPermissions.find((perm: any) => (perm.type || perm.type_id) === typeId)
        ).filter(Boolean);
        
        if (permsForTypes.length === allTypeIds.length && permsForTypes.length > 0) {
          const firstKey = createGroupKey(permsForTypes[0]);
          const allSame = permsForTypes.every(perm => createGroupKey(perm) === firstKey);
          if (allSame) {
            const { male, female } = permsForTypes[0];
            const actions = permsForTypes[0]?.action ? normalizeActions(permsForTypes[0].action) : [];
            remainingPermissions = remainingPermissions.filter((perm: any) => !allTypeIds.includes(perm.type || perm.type_id));
            categoryLevelPermissions.push({ Name: cat.name, name_label: cat.name_label, module: mod.name, actions, male, female, id: cat.id });
          }
        }
      });
    });

    // Build unique categories and types maps
    const uniqueCategoriesMap: Record<string, {id: number; name: string; name_label?: string; moduleId: number; moduleName: string}> = {};
    const uniqueTypesMap: Record<string, {id: number; name: string; name_label: string; categoryId: number; categoryName: string; moduleId: number; moduleName: string}> = {};
    
    permissionsResponse.permissions.forEach((perm: any) => {
      for (const mod of modulesData.objects) {
        for (const cat of mod.categories || []) {
          const foundType = (cat.types || []).find(
            (type) => type.id === (perm.type || perm.type_id)
          );
          if (foundType) {
            const catKey = `${cat.id}-${mod.id}`;
            if (!uniqueCategoriesMap[catKey]) {
              uniqueCategoriesMap[catKey] = {
                id: cat.id,
                moduleId: mod.id,
                moduleName: mod.name,
                name: cat.name,
                name_label: cat.name_label
              };
            }
            const typeKey = `${foundType.id}-${cat.id}-${mod.id}`;
            if (!uniqueTypesMap[typeKey]) {
              uniqueTypesMap[typeKey] = {
                id: foundType.id,
                name: foundType.name,
                name_label: foundType.name_label,
                categoryId: cat.id,
                categoryName: cat.name,
                moduleId: mod.id,
                moduleName: mod.name
              };
            }
          }
        }
      }
    });

    const uniqueCategoriesOfSelectedTypes = Object.values(uniqueCategoriesMap);
    const uniqueTypesOfSelectedTypes = Object.values(uniqueTypesMap);
    const uniqueModuleIds: number[] = Array.from(new Set(uniqueTypesOfSelectedTypes.map(typeObj => typeObj.moduleId)));

    // Build final permissions array
    let finalPermissions: any[] = [];
    moduleLevelPermissions.forEach((obj) => {
      finalPermissions.push({ ...obj, type: 'module' });
    });
    categoryLevelPermissions.forEach((obj) => {
      finalPermissions.push({ ...obj, type: 'category' });
    });
    
    if (Array.isArray(remainingPermissions)) {
      finalPermissions = [
        ...finalPermissions,
        ...remainingPermissions.map((perm: any) => {
          let typeName = '';
          let typeName_label = '';
          for (const mod of modulesData.objects) {
            for (const cat of mod.categories || []) {
              const foundType = (cat.types || []).find(
                (type) => type.id === (perm.type || perm.type_id)
              );
              if (foundType) {
                typeName = foundType.name;
                typeName_label = foundType.name_label;
                break;
              }
            }
            if (typeName) break;
          }
          return {
            ...perm,
            type: 'type',
            Name: typeName,
            name_label: typeName_label
          };
        })
      ];
    }

    return {
      finalPermissions,
      uniqueModuleIds,
      uniqueCategoriesOfSelectedTypes,
      uniqueTypesOfSelectedTypes
    };
  };

  // Helper function to apply processed permissions to state
  const applyProcessedPermissions = (processedData: ReturnType<typeof processPermissionsResponse>, showView = true) => {
    if (!processedData) return;
    
    const { finalPermissions, uniqueModuleIds, uniqueCategoriesOfSelectedTypes, uniqueTypesOfSelectedTypes } = processedData;
    
    setSelectedModules(uniqueModuleIds);
    setSelectedCategories(uniqueCategoriesOfSelectedTypes);
    setSelectedTypes(uniqueTypesOfSelectedTypes);
    setTableRows([
      {
        modules: uniqueModuleIds.slice(),
        categories: uniqueCategoriesOfSelectedTypes.slice(),
        types: uniqueTypesOfSelectedTypes.slice(),
      }
    ]);
    setSelectedModules([]);
    setSelectedCategories([]);
    setSelectedTypes([]);
    
    setRolePermissions(finalPermissions);
    if (showView && finalPermissions.length > 0) {
      setShowViewMode(true);
    } else if (finalPermissions.length === 0) {
      setShowViewMode(false);
    }
  };

  // Dedicated function to fetch and display role permissions (no toggle logic)
  const refetchRolePermissions = async (roleId: number) => {
    const role = rolesData.find(r => r.role_id === roleId);
    if (!role) return;

    setSelectedUsers([]);
    setSelectedUserName(null);
    setSelectedRole(roleId);
    setSelectedRoleName({ name: role.role_name, id: role.role_id });
    
    try {
      const permissionsResponse = await getPermissionsByRole(roleId);
      const processedData = processPermissionsResponse(permissionsResponse);
      
      if (processedData) {
        applyProcessedPermissions(processedData, true);
      } else {
        setRolePermissions(permissionsResponse);
        setShowViewMode(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch role permissions:', error);
      setRolePermissions(null);
    }
  };

  // Dedicated function to fetch and display user permissions (no toggle logic)
  const refetchUserPermissions = async (userId: number) => {
    const userRole = rolesData.find(role => role.users.some(user => user.id === userId));
    if (!userRole) return;

    const user = userRole.users.find(u => u.id === userId);
    
    setSelectedRole(null);
    setSelectedRoleName(null);
    setSelectedUsers([userId]);
    if (user) {
      setSelectedUserName(`${user.first_name} ${user.last_name}`);
    }

    try {
      const permissionsResponse = await getPermissionsByUser(userId);
      const processedData = processPermissionsResponse(permissionsResponse);
      
      if (processedData) {
        applyProcessedPermissions(processedData, true);
      } else {
        setRolePermissions(permissionsResponse);
        setShowViewMode(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch user permissions:', error);
      setRolePermissions(null);
    }
  };

  const handleRoleSelect = async (roleId: number) => {
    const role = rolesData.find(r => r.role_id === roleId);
    if (!role) return;

    if (selectedRole === roleId) {
      setSelectedRole(null);
      setSelectedRoleName(null);
      setRolePermissions(null);
    } else {
      setSelectedUsers([]);
      setSelectedUserName(null);
      setSelectedRole(roleId);
      setSelectedRoleName({ name: role.role_name, id: role.role_id });
      try {
        const permissionsResponse = await getPermissionsByRole(roleId);
        const processedData = processPermissionsResponse(permissionsResponse);
        
        if (processedData) {
          applyProcessedPermissions(processedData, false);
        } else {
          setRolePermissions(permissionsResponse);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch role permissions:', error);
        setRolePermissions(null);
      }
    }
  };

  const handleUserSelect = (userId: number) => {
    const userRole = rolesData.find(role => role.users.some(user => user.id === userId));
    if (userRole) {
      if (selectedRole !== null) {
        setSelectedRole(null);
        setSelectedRoleName(null);
      }
    }

    const isCurrentlySelected = selectedUsers.includes(userId);
    let newSelection: number[] = [];
    if (isCurrentlySelected) {
      newSelection = [];
      setSelectedUserName(null);
    } else {
      newSelection = [userId];
      const user = userRole?.users.find(u => u.id === userId);
      if (user) {
        setSelectedUserName(`${user.first_name} ${user.last_name}`);
      }
      getPermissionsByUser(userId).then((permissionsResponse) => {
        const processedData = processPermissionsResponse(permissionsResponse);
        
        if (processedData) {
          applyProcessedPermissions(processedData, false);
        } else {
          setRolePermissions(permissionsResponse);
        }
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch user permissions:', error);
      });
    }
    setSelectedUsers(newSelection);
  };


  const getGrantedRights = () => {
    const colorMap: Record<string, string> = {
      Clinic: '#4285F4',
      Andrology: '#38D430',
      Embryology: '#38D430',
      Billing: '#FFE666',
      Invoice: '#FFB26B',
    };
    const lastRow = tableRows.length > 0 ? tableRows[tableRows.length - 1] : null;
    if (!lastRow) return [];

    return lastRow.categories.map((cat: any) => ({
      name: cat.name,
      name_label: cat.name_label,
      color: colorMap[cat.name] || '#4285F4',
      permissions: ['add', 'edit', 'view', 'print'],
    }));
  };

  const handleGrantAccess = async (permissions: any) => {
    setGrantedPermissions(permissions);
    setOpenConfirmDialog(true);
  };

  const handleSavePermissions = () => {
    showLoader();
    const extractedPermissions: any[] = [];
    
    if (grantedPermissions && grantedPermissions.modules && Array.isArray(grantedPermissions.modules)) {
      grantedPermissions.modules.forEach((module: any) => {
        if (module.submodules && Array.isArray(module.submodules)) {
          module.submodules.forEach((submodule: any) => {
            if (submodule.type && Array.isArray(submodule.type)) {
              submodule.type.forEach((type: any) => {
                if (type.permissions && Array.isArray(type.permissions)) {
                  type.permissions.forEach((permission: any) => {
                    extractedPermissions.push({
                      type_id: type.id || 0,
                      type_name: type.name,
                      submodule_name: submodule.name,
                      module_name: module.name,
                      action: permission.access.toLowerCase(),
                      male: permission.male,
                      female: permission.female
                    });
                  });
                }
              });
            }
          });
        }
      });
    }
    
    const groupedPermissions = extractedPermissions.reduce((acc: any, permission: any) => {
      const typeId = permission.type_id;
      if (!acc[typeId]) {
        acc[typeId] = {
          type_id: typeId,
          action: [],
          male: permission.male,
          female: permission.female
        };
      }
      // Add the permission action if not already present
      if (!acc[typeId].action.includes(permission.action)) {
        acc[typeId].action.push(permission.action);
      }
      // If action is add/edit/print, ensure 'view' is present
      if (['add', 'edit', 'print'].includes(permission.action)) {
        if (!acc[typeId].action.includes('view')) {
          acc[typeId].action.push('view');
        }
      }
      // If only 'add' is present, ensure 'edit' is also present
      if (permission.action === 'add') {
        if (!acc[typeId].action.includes('edit')) {
          acc[typeId].action.push('edit');
        }
      }
      return acc;
    }, {});

    const clubbedPermissions: Permission[] = Object.values(groupedPermissions) as Permission[];
    
    try {
      if (selectedUsers.length > 0) {
        const userId = selectedUsers[0];
        const transformedPermissions: GrantedUserPermission = {
          user_ids: selectedUsers,
          permissions: clubbedPermissions
        };
        
        postUserPermissions(transformedPermissions)
          .then(async (response) => {
            if (response) {
              await fetchData();
              setOpenConfirmDialog(false);
              addAlert(
                'Permissions updated successfully',
                'success'
              );
              // Use dedicated refetch function to get fresh permissions
              await refetchUserPermissions(userId);
            }
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('User permissions error:', error);
            addAlert(
              'Error updating permissions',
              'error'
            );
          }).finally(() => {
            hideLoader();
          });
      } else if (selectedRoleName) {
        const roleId = selectedRoleName.id;
        const transformedPermissions: GrantedRolePermission = {
          name: selectedRoleName.name || 'Unknown Role',
          permissions: clubbedPermissions
        };

        postRolePermissions(roleId, transformedPermissions)
          .then(async (response) => {
            if (response) {
              await fetchData();
              setOpenConfirmDialog(false);
              addAlert(
                'Permissions updated successfully',
                'success'
              );
              // Use dedicated refetch function to get fresh permissions
              await refetchRolePermissions(roleId);
            }
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Role permissions error:', error);
            addAlert(
              'Error updating permissions',
              'error'
            );
          }).finally(() => {
            hideLoader();
          });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving permissions:', error);
    }
  };

  const handleEditMode = () => {
    setShowViewMode(false);
  };

  useEffect(() => {
    // Automatically open view mode when users or role are selected
    if (selectedRole !== null || selectedUsers.length > 0) {
      setShowViewMode(true);
    } else {
      setShowViewMode(false);
    }
  }, [selectedRole, selectedUsers]);

  return (
    <>
      <Grid2 container spacing={2} sx={{ p: 2 }} style={{ height: 'calc(100vh - 140px)', overflowY: 'auto' }}>
        <Grid2 size={{ xs: 12, md: 4, xl: 3 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, p: 2, height: '100%', border: '1px solid #E0E0E0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: 24 }}>
                {t('UserRights.AllRoles')}
              </Typography>
            </Box>
           
            <Divider sx={{ my: 2 }} />
            <List disablePadding>
              {loading ? (
                <LoaderComponent />
              ) : (
                rolesData.map((role) => (
                  <Box key={role.role_id} sx={{ bgcolor: expandedRoles.includes(role.role_id) ? '#FAFAFA' : 'transparent', borderRadius: 3 }}>
                    <ListItem disableGutters sx={{ p: 1, alignItems: 'flex-start' }}>
                      <Checkbox 
                        checked={selectedRole === role.role_id}
                        onChange={() => handleRoleSelect(role.role_id)}
                        sx={{ p: 0, mr: 1, mt: 0.5 }} 
                      />
                      <ListItemText
                        primary={<Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontFamily: 'Montserrat',
                              fontWeight: 500,
                              fontStyle: 'normal',
                              fontSize: 16,
                              lineHeight: '18px',
                            }}
                          >
                            {role.role_name_label || role.role_name}
                          </Typography>
                          <Typography sx={{ fontSize: 16, color: '#757575', ml: 1 }}>
                            ({role.users.length})
                          </Typography>
                        </Box>} />
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        <Box sx={{ bgcolor: '#FFF3F0', color: '#FF6B6B', borderRadius: '50%', width: '25px', height: '25px', fontSize: 16, fontWeight: 600, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {role.except_count.toString().padStart(2, '0')}
                        </Box>
                        <IconButton size="small" onClick={() => handleExpand(role.role_id)}>
                          {expandedRoles.includes(role.role_id) ? <ExpandLessIcon sx={{ color: '#BDBDBD' }} /> : <AddIcon sx={{ color: '#BDBDBD' }} />}
                        </IconButton>
                      </Box>
                    </ListItem>
                    <Collapse in={expandedRoles.includes(role.role_id)} timeout="auto" unmountOnExit>
                      <List disablePadding sx={{ p: 2, borderRadius: 2 }}>
                        {role.users.map((user) => {
                          const isDisabled = selectedRole === role.role_id;
                          return (
                            <ListItem key={user.id} disableGutters sx={{ px: 2, py: 1, mb: 1, bgcolor: '#FFF', borderRadius: 2 }}>
                              <Tooltip 
                                title={isDisabled ? 'Individual user selection is disabled when the entire role is selected' : ''}
                                arrow
                                placement="top"
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: isDisabled ? 'not-allowed' : 'default' }}>
                                  <Checkbox 
                                    checked={selectedUsers.includes(user.id)} 
                                    disabled={isDisabled}
                                    onChange={() => handleUserSelect(user.id)
                                    }
                                    sx={{ 
                                      p: 0, 
                                      mr: 1,
                                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                                      '&.Mui-disabled': {
                                        cursor: 'not-allowed'
                                      }
                                    }} 
                                  />
                                </Box>
                              </Tooltip>
                              <ListItemText 
                                primary={
                                  <Typography sx={{
                                    fontFamily: 'Nunito',
                                    fontWeight: 500,
                                    fontStyle: 'normal',
                                    fontSize: 14,
                                    lineHeight: 1,
                                    opacity: isDisabled ? 0.6 : 1,
                                    cursor: isDisabled ? 'not-allowed' : 'default'
                                  }}>
                                    {user.first_name} {user.last_name}
                                  </Typography>
                                } 
                              />
                              {user.is_except && (
                                <PersonOutlineIcon style={{ width: '18px', height: '18px' }} />
                              )}
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                ))
              )}
            </List>
          </Paper>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 8, xl: 9 }} sx={{ flex: 1, maxWidth: '100%' }}>
          {showViewMode ? (
            <ViewMode 
              grantedRights={rolePermissions ? rolePermissions : getGrantedRights()} 
              userName={selectedUserName || (selectedRoleName ? selectedRoleName.name : null)} 
              onEdit={handleEditMode}   
            />
          ) : (
            <Paper elevation={0} sx={{ borderRadius: 3, p: 3, width: '100%' }}>
              {(selectedRole !== null || selectedUsers.length > 0) ? (
                <>
                  <Typography variant="h5" sx={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 24, mb: 4, color: '#222' }}>
                    {t('UserRights.PageAccess')}  
                  </Typography>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', borderRadius: 4, boxShadow: '0 2px 8px #0001', p: 2, mb: 4 }}>
                      {stepperSteps.map((step, idx, arr) => {
                        const isCompleted = idx < activeStep;
                        const isActive = activeStep === idx;
                        let isClickable = false;
                        if (idx === 0) {
                          isClickable = true;
                        } else if (idx === 1) {
                          isClickable = selectedModules.length > 0;
                        } else if (idx === 2) {
                          isClickable = selectedCategories.length > 0;
                        }
                        return (
                          <React.Fragment key={step.label}>
                            <Box sx={{ display: 'flex', alignItems: 'center', cursor: isClickable ? 'pointer' : 'not-allowed', opacity: isClickable ? 1 : 0.5 }}
                              onClick={() => {
                                if (!isClickable) return;
                                setActiveStep(idx);
                              }}>
                              <Box sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: isCompleted ? '#4CAF50' : isActive ? '#FFF3F0' : '#BDBDBD',
                                color: isCompleted ? '#fff' : isActive ? '#E25E3E' : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: 18,
                                mr: 1,
                                transition: 'all 0.2s',
                              }}>
                                {isCompleted ? <CheckIcon sx={{ fontSize: 22 }} /> : idx + 1}
                              </Box>
                              <Typography sx={{
                                fontFamily: 'Montserrat',
                                fontWeight: 500,
                                fontSize: 18,
                                color: isActive ? '#E25E3E' : '#757575',
                                mr: 2,
                                transition: 'all 0.2s',
                              }}>{step.label}</Typography>
                            </Box>
                            {idx < arr.length - 1 && (
                              <Box sx={{ flex: 1, height: 2, bgcolor: '#E0E0E0', mx: 1, minWidth: 40 }} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                    {activeStep === 0 && modulesData && (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', xl: '1fr 1fr 1fr 1fr' },
                        gap: { xs: 2, sm: 4, md: 6 },
                        mb: 4,
                        minHeight: 60
                      }}>
                        {modulesData.objects.map((mod) => {
                          const checked = selectedModules.includes(mod.id);
                          return (
                            <Box key={mod.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, md: 0 } }}>
                              <Checkbox
                                checked={checked}
                                onChange={() => {
                                  setSelectedModules((prev) => {
                                    if (checked) {
                                      setSelectedCategories((cats) => cats.filter((cat) => cat.moduleId !== mod.id));
                                      setSelectedTypes((types) => types.filter((type) => type.moduleId !== mod.id));
                                      return prev.filter(i => i !== mod.id);
                                    } else {
                                      const modIdx = modulesData.objects.findIndex((m) => m.id === mod.id);
                                      const newCats = getCategoriesForModule(modIdx);
                                      const newTypes = getTypesForModule(modIdx);
                                      setSelectedCategories((cats) => {
                                        const ids = new Set(cats.map((c) => c.id + '-' + c.moduleId));
                                        return [...cats, ...newCats.filter((c) => !ids.has(c.id + '-' + c.moduleId))];
                                      });
                                      setSelectedTypes((types) => {
                                        const ids = new Set(types.map((t) => t.id + '-' + t.categoryId + '-' + t.moduleId));
                                        return [...types, ...newTypes.filter((t) => !ids.has(t.id + '-' + t.categoryId + '-' + t.moduleId))];
                                      });
                                      return [...prev, mod.id];
                                    }
                                  });
                                } }
                                icon={<Box sx={{ border: '2px solid #E0E0E0', width: 24, height: 24, bgcolor: '#fff' }} />}
                                checkedIcon={<Box sx={{ border: '2px solid #B6E2C2', width: 24, height: 24, bgcolor: '#E6F7EA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                                </Box>}
                                sx={{ p: 0, mr: 1 }} />
                              <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 500, color: '#222', fontSize: { xs: 12, md: 14 } }}>
                                {mod.name_label || mod.name}
                              </Typography>
                            </Box>
                          );
                        })}

                        {(canAdd || canEdit) && (
                          
                          <SaveNextButtonGroup
                            disabled={selectedModules.length === 0}
                            onSave={() => {
                              setTableRows(prev => [
                                ...prev,
                                {
                                  modules: selectedModules.slice(),
                                  categories: selectedCategories.slice(),
                                  types: selectedTypes.slice(),
                                }
                              ]);
                              setSelectedModules([]);
                              setSelectedCategories([]);
                              setSelectedTypes([]);
                              setActiveStep(0);
                            }}
                            onNext={() => setActiveStep(1)}
                            showSelectAll={true}
                            selectAllLabel={t('UserRights.SelectAll')}
                            deselectAllLabel={t('UserRights.DeselectAll')}
                            selectAllChecked={modulesData.objects.length > 0 && selectedModules.length === modulesData.objects.length}
                            selectAllIndeterminate={selectedModules.length > 0 && selectedModules.length < modulesData.objects.length}
                            onSelectAllChange={(checked) => {
                              if (checked) {
                                // Select all modules, categories, and types
                                const allModuleIds = modulesData.objects.map((mod) => mod.id);
                                const allCategories = modulesData.objects.flatMap((mod, modIdx) => getCategoriesForModule(modIdx));
                                const allTypes = modulesData.objects.flatMap((mod, modIdx) => getTypesForModule(modIdx));
                                setSelectedModules(allModuleIds);
                                setSelectedCategories(allCategories);
                                setSelectedTypes(allTypes);
                              } else {
                                // Deselect all
                                setSelectedModules([]);
                                setSelectedCategories([]);
                                setSelectedTypes([]);
                              }
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {activeStep === 1 && modulesData && (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', xl: '1fr 1fr 1fr 1fr' },
                        gap: { xs: 2, sm: 4, md: 6 },
                        mb: 4,
                        minHeight: 60
                      }}>
                        {modulesData.objects
                          .filter((mod) => selectedModules.includes(mod.id))
                          .flatMap((mod) => (mod.categories || []).map((cat) => {
                            const catObj = {
                              id: cat.id,
                              name: cat.name,
                              moduleId: mod.id,
                              moduleName: mod.name
                            };
                            const checked = selectedCategories.some(
                              (c) => c.id === catObj.id && c.moduleId === catObj.moduleId
                            );
                            return (
                              <Box key={catObj.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, md: 0 } }}>
                                <Checkbox
                                  checked={checked}
                                  onChange={() => {
                                    setSelectedCategories((prev) => {
                                      if (checked) {
                                        setSelectedTypes((types) => types.filter((type) => !(type.categoryId === catObj.id && type.moduleId === catObj.moduleId)));
                                        return prev.filter((c) => !(c.id === catObj.id && c.moduleId === catObj.moduleId));
                                      } else {
                                        const modIdx = modulesData.objects.findIndex((m) => m.id === catObj.moduleId);
                                        const newTypes = getTypesForCategory(modIdx, catObj.id);
                                        setSelectedTypes((types) => {
                                          const ids = new Set(types.map((t) => t.id + '-' + t.categoryId + '-' + t.moduleId));
                                          return [...types, ...newTypes.filter((t) => !ids.has(t.id + '-' + t.categoryId + '-' + t.moduleId))];
                                        });
                                        return [...prev, catObj];
                                      }
                                    });
                                  } }
                                  icon={<Box sx={{ border: '2px solid #E0E0E0', width: 24, height: 24, bgcolor: '#fff' }} />}
                                  checkedIcon={<Box sx={{ border: '2px solid #B6E2C2', width: 24, height: 24, bgcolor: '#E6F7EA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                                  </Box>}
                                  sx={{ p: 0, mr: 1 }} />
                                <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 500, color: '#222', fontSize: { xs: 12, md: 14 } }}>
                                  {cat.name_label || cat.name}
                                </Typography>
                              </Box>
                            );
                          })
                          )}

                        {(canAdd || canEdit) && (

                          <SaveNextButtonGroup
                            disabled={selectedCategories.length === 0}
                            onSave={() => {
                              setTableRows(prev => [
                                ...prev,
                                {
                                  modules: selectedModules.slice(),
                                  categories: selectedCategories.slice(),
                                  types: selectedTypes.slice(),
                                }
                              ]);
                              setSelectedModules([]);
                              setSelectedCategories([]);
                              setSelectedTypes([]);
                              setActiveStep(0);
                            }}
                            onNext={() => setActiveStep(2)}
                            showSelectAll={true}
                            selectAllLabel={t('UserRights.SelectAll')}
                            deselectAllLabel={t('UserRights.DeselectAll')}
                            selectAllChecked={categoriesSelectAllChecked}
                            selectAllIndeterminate={categoriesSelectAllIndeterminate}
                            onSelectAllChange={(checked) => {
                              if (checked) {
                                // Select all categories and their types for selected modules
                                const allCategories = modulesData.objects
                                  .filter((mod) => selectedModules.includes(mod.id))
                                  .flatMap((mod) => (mod.categories || []).map((cat) => ({
                                    id: cat.id,
                                    name: cat.name,
                                    moduleId: mod.id,
                                    moduleName: mod.name
                                  })));
                                const allTypes = modulesData.objects
                                  .filter((mod) => selectedModules.includes(mod.id))
                                  .flatMap((mod) => {
                                    const actualModIdx = modulesData.objects.findIndex((m) => m.id === mod.id);
                                    return getTypesForModule(actualModIdx);
                                  });
                                setSelectedCategories(allCategories);
                                setSelectedTypes(allTypes);
                              } else {
                                // Deselect all categories and types
                                setSelectedCategories([]);
                                setSelectedTypes([]);
                              }
                            }}
                          />
                        )} 
                      </Box>
                    )}
                    {activeStep === 2 && selectedModules.length > 0 && modulesData && (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', xl: '1fr 1fr 1fr 1fr' },
                        gap: { xs: 2, sm: 4, md: 6 },
                        mb: 4,
                        minHeight: 60
                      }}>
                        {modulesData.objects
                          .filter((mod) => selectedModules.includes(mod.id))
                          .flatMap((mod) => (mod.categories || [])
                            .filter((cat) => selectedCategories.some((c) => c.id === cat.id && c.moduleId === mod.id))
                            .flatMap((cat) => (cat.types || []).map((type) => {
                              const typeObj = {
                                id: type.id,
                                name: type.name,
                                name_label: type.name_label,
                                categoryId: cat.id,
                                categoryName: cat.name,
                                moduleId: mod.id,
                                moduleName: mod.name
                              };
                              const checked = selectedTypes.some(
                                (t) => t.id === typeObj.id && t.categoryId === typeObj.categoryId && t.moduleId === typeObj.moduleId
                              );
                              return (
                                <Box key={typeObj.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, md: 0 } }}>
                                  <Checkbox
                                    checked={checked}
                                    onChange={() => {
                                      setSelectedTypes((prev) =>
                                        checked
                                          ? prev.filter((t) => !(t.id === typeObj.id && t.categoryId === typeObj.categoryId && t.moduleId === typeObj.moduleId))
                                          : [...prev, typeObj]
                                      );
                                    }}
                                    icon={<Box sx={{ border: '2px solid #E0E0E0', width: 24, height: 24, bgcolor: '#fff' }} />}
                                    checkedIcon={<Box sx={{ border: '2px solid #B6E2C2', width: 24, height: 24, bgcolor: '#E6F7EA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                                    </Box>}
                                    sx={{ p: 0, mr: 1 }}
                                  />
                                  <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 500, color: '#222', fontSize: { xs: 12, md: 14 } }}>
                                    {type.name_label || type.name}
                                  </Typography>
                                </Box>
                              );
                            })
                            )
                          )}
                        {(canAdd || canEdit) && (

                          <SaveNextButtonGroup
                            disabled={selectedTypes.length === 0}
                            onSave={() => {
                              setTableRows(prev => [
                                ...prev,
                                {
                                  modules: selectedModules.slice(),
                                  categories: selectedCategories.slice(),
                                  types: selectedTypes.slice(),
                                }
                              ]);
                              setSelectedModules([]);
                              setSelectedCategories([]);
                              setSelectedTypes([]);
                              setActiveStep(0);
                            }}
                            showNext={false}
                            saveLabel={tGeneral('Save')}
                            showSelectAll={true}
                            selectAllLabel={t('UserRights.SelectAll')}
                            deselectAllLabel={t('UserRights.DeselectAll')}
                            selectAllChecked={typesSelectAllChecked}
                            selectAllIndeterminate={typesSelectAllIndeterminate}
                            onSelectAllChange={(checked) => {
                              if (checked) {
                                // Select all types for selected categories
                                const allTypes = modulesData.objects
                                  .filter((mod) => selectedModules.includes(mod.id))
                                  .flatMap((mod) => (mod.categories || [])
                                    .filter((cat) => selectedCategories.some((c) => c.id === cat.id && c.moduleId === mod.id))
                                    .flatMap((cat) => (cat.types || []).map((type) => ({
                                      id: type.id,
                                      name: type.name,
                                      name_label: type.name_label,
                                      categoryId: cat.id,
                                      categoryName: cat.name,
                                      moduleId: mod.id,
                                      moduleName: mod.name
                                    })))
                                  );
                                setSelectedTypes(allTypes);
                              } else {
                                // Deselect all types
                                setSelectedTypes([]);
                              }
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {tableRows.length > 0 && modulesData && (
                      <UserRightsTable
                        selectedModules={tableRows.flatMap(row => row.modules)}
                        selectedCategories={tableRows.flatMap(row => row.categories)}
                        selectedTypes={ tableRows.flatMap(row => row.types) }
                        moduleData={modulesData}
                        apiActions={rolePermissions || []}
                        onRemove={(typeKey: any, subcategories?: any, moduleObj?: any, categoryObj?: any) => {
                          
                          setTableRows(prev => {
                            const newRows = prev.map((r) => {
                              // Determine what to remove based on typeKey
                              let removeModuleId: number | null = null;
                              let removeCategoryId: number | null = null;
                              let removeSubCategoryIds: number[] = [];
                              if (typeKey.startsWith('module-') && moduleObj && moduleObj.id !== undefined) {
                                removeModuleId = moduleObj.id;
                              } else if (typeKey.startsWith('category-') && categoryObj && categoryObj.id !== undefined) {
                                removeCategoryId = categoryObj.id;
                              } else if (typeKey.startsWith('subcategory-') && subcategories && typeof subcategories === 'object' && subcategories.id !== undefined) {
                                removeSubCategoryIds = [subcategories.id];
                              }
                              return {
                                ...r,
                                modules: removeModuleId !== null
                                  ? r.modules.filter((modId: number) => modId !== removeModuleId)
                                  : r.modules,
                                categories: removeModuleId !== null
                                  ? r.categories.filter((cat: any) => cat.moduleId !== removeModuleId)
                                  : removeCategoryId !== null
                                    ? r.categories.filter((cat: any) => cat.id !== removeCategoryId)
                                    : r.categories,
                                types: removeModuleId !== null
                                  ? r.types.filter((type: any) => type.moduleId !== removeModuleId)
                                  : removeCategoryId !== null
                                    ? r.types.filter((type: any) => type.categoryId !== removeCategoryId)
                                    : removeSubCategoryIds.length > 0
                                      ? r.types.filter((type: any) => !removeSubCategoryIds.includes(type.id))
                                      : r.types,
                              };
                            }).filter(r => r.modules.length > 0 || r.categories.length > 0 || r.types.length > 0);
                            return newRows;
                          });

                          // Remove from rolePermissions for module/category
                          if (rolePermissions && Array.isArray(rolePermissions)) {
                            let updatedRolePermissions = rolePermissions;
                            if (typeKey.startsWith('module-') && moduleObj && moduleObj.id !== undefined) {
                              updatedRolePermissions = updatedRolePermissions.filter((perm: any) => !(perm.type === 'module' && perm.id === moduleObj.id));
                            } else if (typeKey.startsWith('category-') && categoryObj && categoryObj.id !== undefined) {
                              updatedRolePermissions = updatedRolePermissions.filter((perm: any) => !(perm.type === 'category' && perm.id === categoryObj.id));
                            }
                            setRolePermissions(updatedRolePermissions);
                          }
                        }}
                        // onRemove={(typeKey: string) => setTableRows([])}
                        onGrantAccess={handleGrantAccess}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  minHeight: '300px',
                  textAlign: 'center',
                  color: '#757575'
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Montserrat', fontWeight: 500 }}>
                    {t('UserRights.SelectUserOrRole')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'Nunito', opacity: 0.8 }}>
                    {t('UserRights.SelectUserOrRoleDescription')}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid2>
        <ConfirmationModal
          open={openConfirmDialog}
          handleClose={() => setOpenConfirmDialog(false)}
          onConfirm={handleSavePermissions}
          title={t('Confirm')}
          contentMessage={tGeneral('AreYouSureSubmit')}
          type={'warning'}
        />
      </Grid2>
    </>
  );
};

export default UserRights;
